import { useCallback } from "react";
import cloneDeep from "lodash/cloneDeep";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { AdjustedProductType, FundTransferDetails, InvestmentPathDetail, LiquidityPeriodBalanceType, Mimshak, PolicyStatus } from "../../interfaces/DTO/mislaka_xml_models";
import { FundAdditionType, SimulationChangeType } from "../../interfaces/DTO/simulation";
import { FundDataBasic } from "../../interfaces/DTO/FundDataBasic";
import { MimshakDataUtil } from "../../services/mimshakDataUtil";

const EXECUTIVE_INSURANCE_TYPES = new Set([
  AdjustedProductType.ACTIVE_MANAGERS_INSURANCE,
  AdjustedProductType.INACTIVE_MANAGERS_INSURANCE,
  AdjustedProductType.MANDATORY_PENSION_FUND,
  AdjustedProductType.SUPPLEMENTARY_PENSION_FUND,
]);

export interface PathFundSelection {
  fund: FundDataBasic;
  amount: number;
  depositPercentage?: number;
}

export const mergeDuplicateInvestmentPathsByPathCode = (mimshak: MimshakData): MimshakData =>
  MimshakDataUtil.mergeDuplicateInvestmentPathsByPathCode(mimshak);

// Scales investment_paths proportionally to newFundsSum and updates accumulated_savings.
// Works on a shallow copy — does not mutate the input.
export const scalePathsToFundsSum = (mimshak: MimshakData, newFundsSum: number): MimshakData => {
  if (!mimshak.data?.products) return { ...mimshak, funds_sum: newFundsSum };

  const products = mimshak.data.products.map(product => ({
    ...product,
    policies_or_accounts: (product.policies_or_accounts ?? []).map(policy => {
      // Merge duplicate paths by path_code before scaling
      const rawPaths = policy.investment_paths ?? [];
      const mergedPathMap = new Map<number, InvestmentPathDetail>();
      for (const p of rawPaths) {
        const code = p.path_code as number;
        const existing = mergedPathMap.get(code);
        if (existing) {
          mergedPathMap.set(code, { ...existing, accumulated_amount: (existing.accumulated_amount ?? 0) + (p.accumulated_amount ?? 0) });
        } else {
          mergedPathMap.set(code, { ...p });
        }
      }
      const paths = Array.from(mergedPathMap.values());

      const oldTotal = paths.reduce((s, p) => s + (p.accumulated_amount ?? 0), 0);
      const scaledPaths: InvestmentPathDetail[] = paths.map(p => ({
        ...p,
        accumulated_amount: oldTotal > 0
          ? (p.accumulated_amount ?? 0) / oldTotal * newFundsSum
          : paths.length > 0 ? newFundsSum / paths.length : 0,
      }));

      // Merge duplicate accumulated_savings by savings_type_code, then update CAPITAL
      const rawSavings = policy.accumulated_savings ?? [];
      const mergedSavingsMap = new Map<number, typeof rawSavings[0]>();
      for (const s of rawSavings) {
        const key = s.savings_type_code as number;
        const existing = mergedSavingsMap.get(key);
        if (existing) {
          mergedSavingsMap.set(key, { ...existing, total_savings: (existing.total_savings ?? 0) + (s.total_savings ?? 0) });
        } else {
          mergedSavingsMap.set(key, { ...s });
        }
      }
      const savings = Array.from(mergedSavingsMap.values()).map(s => ({
        ...s,
        total_savings: s.savings_type_code === LiquidityPeriodBalanceType.CAPITAL
          ? newFundsSum
          : s.total_savings,
      }));

      const liquiditySummaries = (policy.liquidity_summaries ?? []).map(l => ({
        ...l,
        liquidity_amount: newFundsSum,
      }));

      return { ...policy, investment_paths: scaledPaths, accumulated_savings: savings, liquidity_summaries: liquiditySummaries };
    }),
  }));

  return { ...mimshak, funds_sum: newFundsSum, data: { ...mimshak.data, products } };
};

// Applies a new funds_sum to a MimshakData object:
// - If investment_paths exist: scales them proportionally via scalePathsToFundsSum
// - If no paths: creates an initial path (path_code=0) and upserts CAPITAL in accumulated_savings
export const applyFundsSumToMimshak = (mimshak: MimshakData, newFundsSum: number): MimshakData => {
  const pol0 = mimshak.data?.products?.[0]?.policies_or_accounts?.[0];
  const existingPaths = pol0?.investment_paths ?? [];

  if (existingPaths.length > 0) {
    return scalePathsToFundsSum(mimshak, newFundsSum);
  }

  // No paths yet — create initial path and ensure accumulated_savings has CAPITAL entry
  const existingSavings = pol0?.accumulated_savings ?? [];
  const newSavings = existingSavings.some(s => s.savings_type_code === LiquidityPeriodBalanceType.CAPITAL)
    ? existingSavings.map(s => s.savings_type_code === LiquidityPeriodBalanceType.CAPITAL
        ? { ...s, total_savings: newFundsSum }
        : s)
    : [...existingSavings, { savings_type_code: LiquidityPeriodBalanceType.CAPITAL, total_savings: newFundsSum, deposit_type_code: 0, total_redemption_value: 0 }];

  const initialPath: InvestmentPathDetail = { path_code: 0, path_name: "", accumulated_amount: newFundsSum, management_fee_on_deposit: 0, management_fee_on_savings: 0, other_management_fees: null, net_return: null };

  if (!mimshak.data?.products) return { ...mimshak, funds_sum: newFundsSum };

  const products = mimshak.data.products.map((p, pi) => pi !== 0 ? p : {
    ...p,
    policies_or_accounts: (p.policies_or_accounts ?? []).map((pol, poli) => poli !== 0 ? pol : {
      ...pol,
      investment_paths: [initialPath],
      accumulated_savings: newSavings,
      liquidity_summaries: (pol.liquidity_summaries ?? []).length > 0
        ? pol.liquidity_summaries!.map(l => ({ ...l, liquidity_amount: newFundsSum }))
        : [{ liquidity_date: mimshak.reported_period ?? new Date().toISOString().split('T')[0], liquidity_amount: newFundsSum }],
    }),
  });

  return { ...mimshak, funds_sum: newFundsSum, data: { ...mimshak.data, products } };
};

export const setPathCodeInProduct = (
  mimshak: MimshakData,
  policyNumber: string,
  fundId: number,
  originalPathCode?: number
): MimshakData => {
  if (!mimshak.data?.products) return mimshak;

  for (const product of mimshak.data.products) {
    if (!product.policies_or_accounts) continue;
    for (const policy of product.policies_or_accounts) {
      if (policy.policy_number === policyNumber) {
        if (!policy.investment_paths || policy.investment_paths.length === 0) {
          policy.investment_paths = [{
            path_code: fundId,
            path_name: "",
            accumulated_amount: mimshak.funds_sum ?? 0,
            management_fee_on_deposit: 0,
            management_fee_on_savings: 0,
            other_management_fees: null,
            net_return: null,
          }];
        } else if (originalPathCode !== undefined) {
          policy.investment_paths.forEach((path) => {
            if (path.path_code === originalPathCode) path.path_code = fundId;
          });
        } else {
          policy.investment_paths.forEach((path) => { path.path_code = fundId; });
        }
      }
    }
  }

  const allPathCodes: number[] = [];
  mimshak.data.products.forEach((product) => {
    product.policies_or_accounts?.forEach((policy) => {
      if (policy.policy_number !== policyNumber) return;
      policy.investment_paths?.forEach((path) => {
        if (typeof path.path_code === "number") allPathCodes.push(path.path_code);
      });
    });
  });
  mimshak.fund_ids = Array.from(new Set(allPathCodes));
  return mimshak;
};

export const resetOriginalInvestmentPaths = (
  modified: MimshakData,
  original: MimshakData,
  policyNumber: string
): MimshakData => {
  if (!modified.data?.products) return modified;
  const originalPaths = (original.data?.products ?? [])
    .flatMap(p => p.policies_or_accounts ?? [])
    .find(p => p.policy_number === policyNumber)
    ?.investment_paths ?? [];

  for (const product of modified.data.products) {
    if (!product.policies_or_accounts) continue;
    for (const policy of product.policies_or_accounts) {
      if (policy.policy_number !== policyNumber) continue;
      policy.investment_paths = originalPaths.map(p => ({ ...p }));
    }
  }

  const allPathCodes = (modified.data.products ?? [])
    .flatMap(p => p.policies_or_accounts ?? [])
    .filter(p => p.policy_number === policyNumber)
    .flatMap(p => (p.investment_paths ?? []).map(ip => ip.path_code))
    .filter((c): c is number => typeof c === "number");
  modified.fund_ids = Array.from(new Set(allPathCodes));
  return modified;
};

export const replacePathsInProduct = (
  mimshak: MimshakData,
  policyNumber: string,
  selections: PathFundSelection[]
): MimshakData => {
  if (!mimshak.data?.products) return mimshak;

  for (const product of mimshak.data.products) {
    if (!product.policies_or_accounts) continue;
    for (const policy of product.policies_or_accounts) {
      if (policy.policy_number !== policyNumber) continue;

      // Build new paths purely from snapshot — no reference to old paths
      const newPaths: InvestmentPathDetail[] = selections.map((s): InvestmentPathDetail => ({
        path_code: s.fund.fund_id,
        path_name: s.fund.fund_name ?? "",
        accumulated_amount: s.amount,
        deposit_percentage: s.depositPercentage,
        management_fee_on_deposit: 0,
        management_fee_on_savings: 0,
        other_management_fees: null,
        net_return: null,
      }));

      // Integrity: ensure path amounts sum to funds_sum (authoritative total).
      // funds_sum on the mimshak object takes precedence — accumulated_savings may be stale.
      const authoritative = (mimshak.funds_sum ?? 0)
        || (policy.accumulated_savings ?? []).reduce((s, a) => s + a.total_savings, 0);
      if (authoritative > 0 && newPaths.length > 0) {
        const pathsTotal = newPaths.reduce((s, p) => s + p.accumulated_amount, 0);
        if (pathsTotal > 0 && Math.round(pathsTotal) !== Math.round(authoritative)) {
          const scale = authoritative / pathsTotal;
          newPaths.forEach(p => { p.accumulated_amount = p.accumulated_amount * scale; });
        } else if (pathsTotal === 0) {
          const share = authoritative / newPaths.length;
          newPaths.forEach(p => { p.accumulated_amount = share; });
        }
      }

      policy.investment_paths = newPaths;

      // For new policies only: update the CAPITAL entry to match funds_sum.
      // Only when accumulated_savings contains CAPITAL alone — if other savings types
      // (NON_PAYING_PENSION etc.) are present, funds_sum is the grand total, not just capital,
      // so writing it into CAPITAL would inflate that component.
      if (mimshak.new_policy && authoritative > 0 && policy.accumulated_savings && policy.accumulated_savings.length > 0) {
        const hasOnlyCapital = policy.accumulated_savings.every(
          s => s.savings_type_code === LiquidityPeriodBalanceType.CAPITAL
        );
        if (hasOnlyCapital) {
          policy.accumulated_savings = policy.accumulated_savings.map(s =>
            s.savings_type_code === LiquidityPeriodBalanceType.CAPITAL
              ? { ...s, total_savings: authoritative }
              : s
          );
        }
      }
    }
  }

  const allPathCodes: number[] = [];
  mimshak.data.products.forEach((product) => {
    product.policies_or_accounts?.forEach((policy) => {
      if (policy.policy_number !== policyNumber) return;
      policy.investment_paths?.forEach((path) => {
        if (typeof path.path_code === "number") allPathCodes.push(path.path_code);
      });
    });
  });
  mimshak.fund_ids = Array.from(new Set(allPathCodes));
  return mimshak;
};

export const setMultiplePathsInProduct = (
  mimshak: MimshakData,
  policyNumber: string,
  selections: PathFundSelection[]
): MimshakData => {
  if (!mimshak.data?.products) return mimshak;

  for (const product of mimshak.data.products) {
    if (!product.policies_or_accounts) continue;
    for (const policy of product.policies_or_accounts) {
      if (policy.policy_number !== policyNumber) continue;

      const makePathEntry = (s: PathFundSelection): InvestmentPathDetail => ({
        path_code: s.fund.fund_id,
        path_name: s.fund.fund_name ?? "",
        accumulated_amount: s.amount ?? 0,
        deposit_percentage: s.depositPercentage,
        management_fee_on_deposit: 0,
        management_fee_on_savings: 0,
        other_management_fees: null,
        net_return: null,
      });

      policy.investment_paths = selections.map(makePathEntry);
    }
  }

  const allPathCodes: number[] = [];
  mimshak.data.products.forEach((product) => {
    product.policies_or_accounts?.forEach((policy) => {
      if (policy.policy_number !== policyNumber) return;
      policy.investment_paths?.forEach((path) => {
        if (typeof path.path_code === "number") allPathCodes.push(path.path_code);
      });
    });
  });
  mimshak.fund_ids = Array.from(new Set(allPathCodes));
  return mimshak;
};


interface UseNewProductHandlersParams {
  contactId: number;
  originalData: MimshakData[];
  modifiedRows: MimshakData[];
  setModifiedRows: React.Dispatch<React.SetStateAction<MimshakData[]>>;
  setManualAssetChips: React.Dispatch<React.SetStateAction<MimshakData[]>>;
  excludedOriginalRows?: MimshakData[];
  setExcludedModifiedRows?: React.Dispatch<React.SetStateAction<MimshakData[]>>;
}

export const useNewProductHandlers = ({
  contactId,
  originalData,
  modifiedRows,
  setModifiedRows,
  setManualAssetChips,
  excludedOriginalRows = [],
  setExcludedModifiedRows,
}: UseNewProductHandlersParams) => {

  const handleCreateNewMimshak = useCallback((
    handlerCode: string,
    fundSum: number,
    fundType: FundAdditionType,
    productType: number | null,
    report_period: string,
    sourceRow?: MimshakData | null,
    overrideContactId?: number | null,
  ): MimshakData => {
    const effectiveContactId = overrideContactId ?? contactId;
    const isRecurrent = fundType === 'recurrent';
    const policy_number = (sourceRow ? "transfered-" : "generated-") + Math.random().toString(36).substring(2, 9);
    const today = new Date().toISOString().split('T')[0];
    const fundsTransfer: FundTransferDetails[] | undefined = sourceRow ? [{
      transferring_corporation: sourceRow.handler_code,
      transferring_fund_id: (sourceRow.adjusted_product_type ?? sourceRow.product_type) as unknown as number,
      transferring_fund_name: sourceRow.policy_number,
      payment_period_code: String(sourceRow.id),
      deposit_date: today,
      deposit_type_code: 0,
      contribution_type: 0,
      depositor_type: 0,
      deposit_amount_paid: fundSum,
      salary_base: 0,
      salary_month: report_period,
      payment_period: 0,
      fixing_date: today,
    }] : undefined;

    const mimshak: Mimshak = {
      products: [{
        details: {
          handler_code: handlerCode,
          product_type: 2,
          producer_code: sourceRow?.handler_code ?? "",
          adjusted_product_type: productType,
        },
        policies_or_accounts: [{
          policy_number: policy_number,
          status: PolicyStatus.ACTIVE,
          investment_paths: sourceRow?.data?.products?.[0]?.policies_or_accounts?.[0]?.investment_paths ?? [],
          management_fee_details: sourceRow?.data?.products?.[0]?.policies_or_accounts?.[0]?.management_fee_details,
          retirement_savings_by_age: sourceRow?.data?.products?.[0]?.policies_or_accounts?.[0]?.retirement_savings_by_age,
          program_name: "",
          update_date: report_period,
          accumulated_savings: (() => {
            const sourceSavings = sourceRow?.data?.products?.[0]?.policies_or_accounts?.[0]?.accumulated_savings;
            const isCapitalOnly = (sourceRow as MimshakData & { _capitalOnly?: boolean } | null | undefined)?._capitalOnly;
            if (isCapitalOnly && sourceSavings) {
              // Keep only the CAPITAL component, updated to the transferred amount
              const capitalEntry = sourceSavings.find(s => s.savings_type_code === LiquidityPeriodBalanceType.CAPITAL);
              return capitalEntry
                ? [{ ...capitalEntry, total_savings: fundSum }]
                : [{ savings_type_code: LiquidityPeriodBalanceType.CAPITAL, total_savings: fundSum, deposit_type_code: 0, total_redemption_value: 0 }];
            }
            return sourceSavings?.map(s =>
              s.savings_type_code === LiquidityPeriodBalanceType.PAYING_PENSION
                ? { ...s, savings_type_code: LiquidityPeriodBalanceType.NON_PAYING_PENSION }
                : s
            ) ?? [{
              savings_type_code: LiquidityPeriodBalanceType.CAPITAL,
              total_savings: isRecurrent ? 0 : fundSum,
              deposit_type_code: 0,
              total_redemption_value: 0,
            }];
          })(),
          liquidity_summaries: [{
            liquidity_date: today,
            liquidity_amount: 0,
          }],
          funds_transfer: fundsTransfer,
        }],
      }],
    };
    const sourceCapitalOnly = (sourceRow as MimshakData & { _capitalOnly?: boolean } | null | undefined)?._capitalOnly;
    const newItem: MimshakData & { _capitalOnly?: boolean; _baseFundsSum: number } = {
      id: Date.now(),
      policy_number: policy_number,
      handler_code: handlerCode,
      product_type: productType,
      saving_fee: null,
      funds_sum: isRecurrent ? 0 : fundSum,
      _baseFundsSum: isRecurrent ? 0 : fundSum,
      agent_code: null,
      contact_id: effectiveContactId,
      reported_period: report_period,
      data: mimshak,
      adjusted_product_type: productType as any,
      new_policy: true,
      deposit_fee: 0,
      deposit_amount: fundType === 'deposit' ? fundSum : 0,
      withdrawal_amount: 0,
      recurrent_deposit: isRecurrent ? fundSum : undefined,
      report_origin: 2,
      report_type: 1,
      ...(sourceCapitalOnly ? { _capitalOnly: true } : {}),
    };

    setModifiedRows(prev => [...prev, newItem]);

    // If source is executive insurance, update excludedModifiedRows with reduced funds_sum
    if (sourceRow && EXECUTIVE_INSURANCE_TYPES.has(sourceRow.adjusted_product_type as AdjustedProductType) && setExcludedModifiedRows) {
      setExcludedModifiedRows(prev => {
        const existing = prev.find(r =>
          r.policy_number === sourceRow.policy_number &&
          r.handler_code === sourceRow.handler_code &&
          (r.adjusted_product_type ?? r.product_type) === (sourceRow.adjusted_product_type ?? sourceRow.product_type)
        );
        const base = existing ?? sourceRow;
        const isCapitalOnly = !!(newItem as MimshakData & { _capitalOnly?: boolean })._capitalOnly;
        const originalRow = excludedOriginalRows.find(r =>
          r.policy_number === sourceRow.policy_number &&
          r.handler_code === sourceRow.handler_code &&
          (r.adjusted_product_type ?? r.product_type) === (sourceRow.adjusted_product_type ?? sourceRow.product_type)
        );
        const baseFundsSum = existing?.funds_sum ?? originalRow?.funds_sum ?? sourceRow.funds_sum ?? 0;
        const currentFundsSum = isCapitalOnly ? baseFundsSum : (existing?.funds_sum ?? sourceRow.funds_sum ?? 0);

    

        const newFundsSum = Math.max(0, currentFundsSum - fundSum);
        const isFullTransfer = newFundsSum === 0;
        const policy = base.data?.products?.[0]?.policies_or_accounts?.[0];
        const modifiedPolicy = policy ? {
          ...policy,
          investment_paths: isFullTransfer
            ? (policy.investment_paths ?? []).map(p => ({ ...p, accumulated_amount: 0 }))
            : isCapitalOnly
              ? (() => {
                  // Reduce investment_paths proportionally by the capital fraction transferred
                  const totalCapital = (policy.accumulated_savings ?? [])
                    .filter(s => s.savings_type_code === LiquidityPeriodBalanceType.CAPITAL)
                    .reduce((sum, s) => sum + (s.total_savings ?? 0), 0);
                  const ratio = totalCapital > 0 ? Math.max(0, (totalCapital - fundSum) / totalCapital) : 1;
                  return (policy.investment_paths ?? []).map(p => ({ ...p, accumulated_amount: Math.round(p.accumulated_amount * ratio) }));
                })()
              : (policy.investment_paths ?? []),
          accumulated_savings: isFullTransfer
            ? (policy.accumulated_savings ?? []).map(s => ({ ...s, total_savings: 0 }))
            : isCapitalOnly
              ? (policy.accumulated_savings ?? []).map(s =>
                  s.savings_type_code === LiquidityPeriodBalanceType.CAPITAL
                    ? { ...s, total_savings: Math.max(0, (s.total_savings ?? 0) - fundSum) }
                    : s
                )
              : (policy.accumulated_savings ?? []),
        } : policy;
        const updatedRow: MimshakData = {
          ...base,
          funds_sum: newFundsSum,
          _changeTypes: isFullTransfer ? [SimulationChangeType.CLOSE_AND_MOVE] : [SimulationChangeType.PARTIAL_TRANSFER],
          data: base.data ? {
            ...base.data,
            products: base.data.products ? [{
              ...base.data.products[0],
              policies_or_accounts: base.data.products[0].policies_or_accounts ? [
                { ...base.data.products[0].policies_or_accounts[0], ...modifiedPolicy },
                ...base.data.products[0].policies_or_accounts.slice(1),
              ] : base.data.products[0].policies_or_accounts,
            }] : base.data.products,
          } : base.data,
        } as MimshakData;
        return existing
          ? prev.map(r => r.policy_number === sourceRow.policy_number ? updatedRow : r)
          : [...prev, updatedRow];
      });
    }

    return newItem;
  }, [contactId, setModifiedRows, excludedOriginalRows, setExcludedModifiedRows]);

  const createSplitMimshak = useCallback((
    sourceMimshak: MimshakData,
    splitAmount: number,
  ): MimshakData => {
    const policy_number = "transfered-" + Math.random().toString(36).substring(2, 9);
    const cloned = cloneDeep(sourceMimshak);
    const scaled = applyFundsSumToMimshak(cloned, splitAmount);

    const today = new Date().toISOString().split('T')[0];
    const fundsTransfer: FundTransferDetails[] = [{
      transferring_corporation: sourceMimshak.handler_code,
      transferring_fund_id: (sourceMimshak.adjusted_product_type ?? sourceMimshak.product_type) as unknown as number,
      transferring_fund_name: sourceMimshak.policy_number,
      payment_period_code: String(sourceMimshak.id),
      deposit_date: today,
      deposit_type_code: 0,
      contribution_type: 0,
      depositor_type: 0,
      deposit_amount_paid: splitAmount,
      salary_base: 0,
      salary_month: sourceMimshak.reported_period,
      payment_period: 0,
      fixing_date: today,
    }];

    if (scaled.data?.products?.[0]?.policies_or_accounts?.[0]) {
      scaled.data.products[0].policies_or_accounts[0].policy_number = policy_number;
      scaled.data.products[0].policies_or_accounts[0].funds_transfer = fundsTransfer;
    }

    const newItem: MimshakData & { _baseFundsSum: number } = {
      ...scaled,
      id: Date.now(),
      policy_number,
      funds_sum: splitAmount,
      _baseFundsSum: splitAmount,
      new_policy: true,
      saving_fee: null,
      deposit_amount: 0,
      withdrawal_amount: 0,
      transfer_in: splitAmount,
    };

    setModifiedRows(prev => [...prev, newItem]);
    return newItem;
  }, [setModifiedRows]);

const handleUpdateNewProduct = useCallback((updatedProduct: Partial<MimshakData>) => {
    setModifiedRows(prev => {
      const idx = prev.findIndex(r => r.policy_number === updatedProduct.policy_number);
      if (idx === -1) return prev;

      const original = prev[idx];
      const newProductType =
        updatedProduct.product_type ??
        original.product_type ??
        original.data?.products?.[0]?.details?.product_type ??
        null;

      const baseData = updatedProduct.data ?? original.data;
      const updatedItem: MimshakData = {
        ...original,
        ...updatedProduct,
        product_type: newProductType,
        adjusted_product_type: newProductType,
        data: {
          ...baseData,
          products: (baseData.products ?? original.data.products).map((p, i) =>
            i === 0
              ? {
                  ...p,
                  details: {
                    ...p.details,
                    product_type: newProductType,
                    adjusted_product_type: newProductType,
                  },
                }
              : p
          ),
        },
      };

      const next = [...prev];
      next[idx] = updatedItem;
      return next; // מכאן grouped יתעדכן אוטומטית
    });
  }, [setModifiedRows]);

 

  const handleCreateNewInsuranceMimshak = useCallback((
    handlerCode: string,
    adjustedProductType: AdjustedProductType,
    reportPeriod: string,
    overrideContactId?: number | null,
  ): MimshakData => {
    const effectiveContactId = overrideContactId ?? contactId;
    const policyNumber = "generated-" + Math.random().toString(36).substring(2, 9);
    const mimshak: Mimshak = {
      products: [{
        details: {
          handler_code: handlerCode,
          product_type: adjustedProductType,
          producer_code: "",
          adjusted_product_type: adjustedProductType,
        },
        policies_or_accounts: [{
          policy_number: policyNumber,
          status: PolicyStatus.ACTIVE,
          investment_paths: [],
          program_name: "",
          update_date: reportPeriod,
          accumulated_savings: [],
          liquidity_summaries: [],
          insurence_coverages: [{
            basic_insurance: [{ insurance_amount: 0, requested_insurance_amount: 0, salary_multiples: 0 }],
            coverage_details: {
              insurance_fees: { fee: 0 },
            },
          } as any],
        }],
      }],
    };
    const newItem: MimshakData = {
      id: Date.now(),
      policy_number: policyNumber,
      handler_code: handlerCode,
      product_type: adjustedProductType,
      adjusted_product_type: adjustedProductType,
      contact_id: effectiveContactId,
      reported_period: reportPeriod,
      data: mimshak,
      new_policy: true,
      saving_fee: null,
      funds_sum: 0,
      deposit_fee: 0,
      deposit_amount: 0,
      withdrawal_amount: 0,
      report_origin: 2,
      report_type: 1,
    };
    setModifiedRows((prev) => [...prev, newItem]);
    return newItem;
  }, [contactId, setModifiedRows]);

  const handleUpdateInsuranceFields = useCallback((
    policyNumber: string,
    primaryInsurance: number | null,
    secondaryInsurance: number | null,
    monthlyCost: number | null,
  ) => {
    console.log(`[handleUpdateInsuranceFields] policyNumber=${policyNumber} primary=${primaryInsurance} secondary=${secondaryInsurance} cost=${monthlyCost}`);
    setModifiedRows(prev => {
      const idx = prev.findIndex(r => r.policy_number === policyNumber);
      if (idx === -1) {
        console.warn(`[handleUpdateInsuranceFields] policy ${policyNumber} NOT FOUND in modifiedRows (length=${prev.length})`);
        return prev;
      }
      const original = prev[idx];
      const coveragesBefore = original.data?.products?.[0]?.policies_or_accounts?.[0]?.insurence_coverages ?? [];
      console.log(`[handleUpdateInsuranceFields] coverages before update (count=${coveragesBefore.length}):`, coveragesBefore.map((c: any) => ({
        kind: c.coverage_details?.insurance_kind,
        insurance_sum: c.coverage_details?.insurance_sum,
        basic_insurance: c.basic_insurance,
      })));
      const updated: MimshakData = {
        ...original,
        data: original.data ? {
          ...original.data,
          products: (original.data.products ?? []).map((product) => ({
            ...product,
            policies_or_accounts: (product.policies_or_accounts ?? []).map((pol) => {
              if (pol.policy_number !== policyNumber) return pol;
              const coverages = pol.insurence_coverages ?? [];
              const updatedCoverages = coverages.map((cov: any, idx: number) => {
                const isFirst = idx === 0;
                const isSecond = idx === 1;
                const newBasicInsurance = isFirst && primaryInsurance !== null
                  ? [{ ...(cov.basic_insurance?.[0] ?? {}), insurance_amount: primaryInsurance }]
                  : isSecond && secondaryInsurance !== null
                  ? [{ ...(cov.basic_insurance?.[0] ?? {}), insurance_amount: secondaryInsurance }]
                  : cov.basic_insurance;

                // also update insurance_sum so InsuranceSummary (which reads insurance_sum) picks up the change
                const newCoverageDetails = cov.coverage_details ? {
                  ...cov.coverage_details,
                  insurance_sum: isFirst && primaryInsurance !== null
                    ? primaryInsurance
                    : isSecond && secondaryInsurance !== null
                    ? secondaryInsurance
                    : cov.coverage_details.insurance_sum,
                  insurance_fees: isFirst && monthlyCost !== null
                    ? { ...(cov.coverage_details.insurance_fees ?? {}), fee: monthlyCost }
                    : cov.coverage_details.insurance_fees,
                } : cov.coverage_details;

                return {
                  ...cov,
                  basic_insurance: newBasicInsurance,
                  coverage_details: newCoverageDetails,
                };
              });
              console.log(`[handleUpdateInsuranceFields] coverages after update (count=${updatedCoverages.length}):`, updatedCoverages.map((c: any) => ({
                kind: c.coverage_details?.insurance_kind,
                insurance_sum: c.coverage_details?.insurance_sum,
                basic_insurance: c.basic_insurance,
              })));
              return { ...pol, insurence_coverages: updatedCoverages };
            }),
          })),
        } : original.data,
      };
      const next = [...prev];
      next[idx] = updated;
      return next;
    });
  }, [setModifiedRows]);

  const handleDeleteNewProduct = useCallback((policyNumber: string) => {
    setModifiedRows(prev => {
      const next = prev.filter(r => r.policy_number !== policyNumber);
      // Recompute excludedModifiedRows from remaining transfered- policies
      if (setExcludedModifiedRows) {
        const transfersBySourceKey = new Map<string, { totalAmount: number }>();
        for (const row of next) {
          const transfer = row.data?.products?.[0]?.policies_or_accounts?.[0]?.funds_transfer?.[0];
          if (!transfer) continue;
          if (!EXECUTIVE_INSURANCE_TYPES.has(transfer.transferring_fund_id as unknown as AdjustedProductType)) continue;
          const sourcePolicyNumber = transfer.transferring_fund_name ?? "";
          const sourceHandlerCode = transfer.transferring_corporation ?? "";
          const sourceProductType = String(transfer.transferring_fund_id ?? "");
          const sourceKey = `${sourcePolicyNumber}|${sourceHandlerCode}|${sourceProductType}`;
          const prev2 = transfersBySourceKey.get(sourceKey) ?? { totalAmount: 0 };
          transfersBySourceKey.set(sourceKey, { totalAmount: prev2.totalAmount + (transfer.deposit_amount_paid ?? 0) });
        }
        setExcludedModifiedRows(
          excludedOriginalRows
            .map((row): MimshakData | null => {
              const rowKey = `${row.policy_number}|${row.handler_code ?? ""}|${String(row.adjusted_product_type ?? row.product_type ?? "")}`;
              const entry = transfersBySourceKey.get(rowKey);
              if (!entry) return null;
              const newFundsSum = Math.max(0, (row.funds_sum ?? 0) - entry.totalAmount);
              const isFullTransfer = newFundsSum === 0;
              const policy = row.data?.products?.[0]?.policies_or_accounts?.[0];
              const modifiedPolicy = policy ? {
                ...policy,
                investment_paths: (policy.investment_paths ?? []).map(p => ({ ...p, accumulated_amount: isFullTransfer ? 0 : p.accumulated_amount })),
                accumulated_savings: (policy.accumulated_savings ?? []).map(s => ({ ...s, total_savings: isFullTransfer ? 0 : s.total_savings })),
              } : policy;
              return {
                ...row,
                funds_sum: newFundsSum,
                _changeTypes: isFullTransfer ? [SimulationChangeType.CLOSE_AND_MOVE] : [SimulationChangeType.PARTIAL_TRANSFER],
                data: row.data ? {
                  ...row.data,
                  products: row.data.products ? [{
                    ...row.data.products[0],
                    policies_or_accounts: row.data.products[0].policies_or_accounts ? [
                      { ...row.data.products[0].policies_or_accounts[0], ...modifiedPolicy },
                      ...row.data.products[0].policies_or_accounts.slice(1),
                    ] : row.data.products[0].policies_or_accounts,
                  }] : row.data.products,
                } : row.data,
              } as MimshakData;
            })
            .filter((r): r is MimshakData => r !== null)
        );
      }
      return next;
    });
  }, [setModifiedRows, setExcludedModifiedRows, excludedOriginalRows]);

  // Handles assets added from the managers insurance dialog.
  // For capital-only transfers (_capitalOnly), deducts the capital amount from the source policy
  // in modifiedRows so it continues to appear with its updated balance.
  // All items are added as draggable chips to create a new product.
  const handleManagersInsuranceAssetsAdded = useCallback((items: MimshakData[]) => {
    const chips: MimshakData[] = [];
    for (const item of items) {
      const capitalOnly = (item as MimshakData & { _capitalOnly?: boolean })._capitalOnly;
      if (capitalOnly) {
        setModifiedRows(prev => {
          const existing = prev.find(r => r.policy_number === item.policy_number);
          const base = existing ?? originalData.find(r => r.policy_number === item.policy_number);
          if (!base) return prev;
          const capitalAmount = item.funds_sum ?? 0;
          const updatedSavings = (base.data?.products?.[0]?.policies_or_accounts?.[0]?.accumulated_savings ?? [])
            .map(s => s.savings_type_code === LiquidityPeriodBalanceType.CAPITAL
              ? { ...s, total_savings: Math.max(0, (s.total_savings ?? 0) - capitalAmount) }
              : s
            );
          const updated: MimshakData = {
            ...base,
            funds_sum: Math.max(0, (base.funds_sum ?? 0) - capitalAmount),
            data: base.data ? {
              ...base.data,
              products: base.data.products?.map((p, pi) => pi === 0 ? {
                ...p,
                policies_or_accounts: p.policies_or_accounts?.map((pol, poli) => poli === 0 ? {
                  ...pol,
                  accumulated_savings: updatedSavings,
                } : pol),
              } : p),
            } : base.data,
          };
          if (existing) return prev.map(r => r.policy_number === item.policy_number ? updated : r);
          return prev;
        });
      }
      chips.push(item);
    }
    setManualAssetChips(prev => [...prev, ...chips]);
  }, [originalData, setModifiedRows, setManualAssetChips]);

  return {
    handleCreateNewMimshak,
    createSplitMimshak,
    handleCreateNewInsuranceMimshak,
    handleUpdateNewProduct,
    handleDeleteNewProduct,
    handleManagersInsuranceAssetsAdded,
    handleUpdateInsuranceFields,
  };
};
